import { ApiError } from "@khazzin/api-client";
import type { Translate } from "@khazzin/i18n";
import { AlertCircle, Clock, Lock, RefreshCw, Wifi } from "lucide-react";
import { type ReactNode, useEffect, useState } from "react";
import { Icon } from "../../icons/Icon";
import { Forklift } from "../../illustrations/Forklift";
import { Button } from "../../primitives/Button";
import { cx } from "../../styles";

/**
 * The six resolutions every screen has.
 *
 * A list is loading, empty, broken, refused, missing, or full of rows. Naming
 * them as components rather than leaving each screen to improvise is what makes
 * "the page looks wrong" answerable: there are six shapes and the screen is in
 * one of them.
 *
 * All of these render *inside* the shell. A screen that cannot be read is not a
 * reason to take away the navigation — somebody who lands on a 403 needs the
 * sidebar to go somewhere else, and a full-page takeover leaves them with the
 * back button.
 */

export interface EmptyStateProps {
	title: string;
	body?: string;
	/** The one thing to do about it. A second action makes neither obvious. */
	action?: ReactNode;
	/** `none` for a small empty state inside a card, where a drawing would crowd it. */
	illustration?: "forklift" | "none";
	className?: string;
}

/**
 * Nothing here yet.
 *
 * Distinct from a *filtered* empty result, which is a different sentence: "no
 * products yet" invites somebody to add one, and "no products match these
 * filters" invites them to clear the filters. A screen that shows the first
 * when it means the second sends people looking for a button that will not
 * help. The caller supplies the copy, so both are one component.
 */
export function EmptyState({
	title,
	body,
	action,
	illustration = "forklift",
	className,
}: EmptyStateProps) {
	return (
		<div className={cx("flex flex-col items-center gap-3 px-5 py-8 text-center", className)}>
			{illustration === "forklift" && (
				<span className="grid size-24 place-items-center rounded-full bg-brand-subtle">
					<Forklift size={56} className="text-accent" />
				</span>
			)}
			<div className="flex flex-col gap-1">
				<p className="text-[14.5px] font-bold text-fg">{title}</p>
				{body !== undefined && (
					<p className="max-w-[280px] text-[12.5px] leading-relaxed text-fg-muted">{body}</p>
				)}
			</div>
			{action}
		</div>
	);
}

export interface ErrorStateProps {
	error: unknown;
	onRetry?: () => void;
	t: Translate;
	/** A one-line variant, for a dashboard tile or a table cell. */
	compact?: boolean;
	/** Overrides the generic heading with something the screen knows. */
	title?: string;
	className?: string;
}

/**
 * Something failed on our side.
 *
 * **The status and the code are shown, in monospace.** Not decoration: a
 * support conversation that starts "it says 503 upstream_unavailable" is one
 * somebody can act on, and "something went wrong" is one that begins with
 * twenty minutes of reproduction. The trace id is deliberately *not* shown —
 * it is in the log, and a person reading a screen has no use for a UUID.
 *
 * Retry is offered whenever the caller can retry. A 4xx will fail the same way
 * a second time, but a caller who passes `onRetry` knows something this
 * component does not, and a missing button on a transient failure is worse than
 * a useless one.
 */
export function ErrorState({
	error,
	onRetry,
	t,
	compact = false,
	title,
	className,
}: ErrorStateProps) {
	const code = error instanceof ApiError ? `${error.status} ${error.code}` : null;
	const message = error instanceof Error ? error.message : t("errors.internal");

	return (
		<div
			role="alert"
			className={cx(
				"flex gap-3 rounded-sm border border-danger-border bg-danger-bg p-3 text-danger-fg",
				className,
			)}
		>
			<Icon as={AlertCircle} size={compact ? 17 : 20} className="mt-px shrink-0" />
			<div className="min-w-0 flex-1">
				<p className={cx("font-bold", compact ? "text-[12.5px]" : "text-[13.5px]")}>
					{title ?? t("ui.state.errorTitle")}
				</p>
				<p className="mt-0.5 text-[12.5px] leading-relaxed">
					{message}
					{code !== null && <span className="ms-1 font-mono">{code}</span>}
				</p>
				{onRetry !== undefined && (
					<Button
						size="sm"
						variant="secondary"
						icon={RefreshCw}
						onClick={onRetry}
						className="mt-2 border-danger-border text-danger-fg"
					>
						{t("ui.actions.retry")}
					</Button>
				)}
			</div>
		</div>
	);
}

export interface ForbiddenStateProps {
	t: Translate;
	/** The permission the server named, rendered in monospace. */
	permission?: string | null;
	/** Set when the refusal was `section_hidden` rather than a missing permission. */
	sectionHidden?: boolean;
	className?: string;
}

/**
 * The server said no.
 *
 * Two different sentences, because they ask for two different things. A missing
 * **permission** is something an owner can grant, and naming it in monospace is
 * what turns "I can't open Finance" into "I need finance.read" — a request an
 * administrator can act on without guessing which of a hundred and forty-seven
 * it was.
 *
 * A hidden **section** is not that. Somebody's administrator turned the module
 * off for their account, and no permission would help; telling them to ask for
 * one would send them to a person who would have to explain the grid. So the
 * copy says who to ask and what happened, and names nothing.
 *
 * Neither says "you do not have permission" and stops. The UI hides; the API
 * enforces. Saying so is what keeps somebody from believing they have found a
 * bug in the navigation.
 */
export function ForbiddenState({
	t,
	permission,
	sectionHidden = false,
	className,
}: ForbiddenStateProps) {
	return (
		<div className={cx("flex flex-col items-center gap-3 px-5 py-8 text-center", className)}>
			<Icon as={Lock} size={40} className="text-fg-subtle" />
			<p className="text-[14.5px] font-bold text-fg">
				{t(sectionHidden ? "ui.state.sectionHiddenTitle" : "ui.state.forbiddenTitle")}
			</p>
			<p className="max-w-[320px] text-[12.5px] leading-relaxed text-fg-muted">
				{sectionHidden
					? t("ui.state.sectionHiddenBody")
					: permission
						? t("ui.state.forbiddenBodyNamed")
						: t("ui.state.forbiddenBody")}
			</p>
			{!sectionHidden && permission && (
				<code className="font-mono text-[12px] text-fg">{permission}</code>
			)}
		</div>
	);
}

export interface NotFoundStateProps {
	t: Translate;
	/** Where "back" goes. Usually the list this record would have been in. */
	action?: ReactNode;
	className?: string;
}

/** The record is not there — deleted, or a mistyped id. */
export function NotFoundState({ t, action, className }: NotFoundStateProps) {
	return (
		<EmptyState
			title={t("ui.state.notFoundTitle")}
			body={t("ui.state.notFoundBody")}
			{...(action === undefined ? {} : { action })}
			{...(className === undefined ? {} : { className })}
		/>
	);
}

export type ConnectionStatus = "live" | "reconnecting" | "offline";

export interface OfflinePillProps {
	status: ConnectionStatus;
	t: Translate;
}

/** How long "Live" stays on screen after a reconnection. */
export const LIVE_PILL_MS = 2_000;

/**
 * A pill in the corner when the live connection is not healthy.
 *
 * It appears when something is wrong and — briefly — when it comes back.
 * `10/05` §7 asks for the reassurance: somebody who watched "Reconnecting…" for
 * thirty seconds needs to be told it worked, and a pill that simply vanished
 * would leave them wondering whether it had.
 *
 * Two seconds, then gone. A permanent "Live" badge is a permanent distraction
 * that stops being read within a day, which is exactly when it would need to be.
 *
 * `role="status"` rather than `alert`: a connection blip is worth announcing
 * politely, and interrupting whatever a screen reader is saying for one would
 * be worse than the blip.
 */
export function OfflinePill({ status, t }: OfflinePillProps) {
	const [showLive, setShowLive] = useState(false);
	const [everDegraded, setEverDegraded] = useState(false);

	useEffect(() => {
		if (status !== "live") {
			setEverDegraded(true);
			setShowLive(false);
			return;
		}
		// Only after a problem. Nobody needs to be told the connection worked on
		// the page they just opened.
		if (!everDegraded) {
			return;
		}
		setShowLive(true);
		const timer = window.setTimeout(() => setShowLive(false), LIVE_PILL_MS);
		return () => window.clearTimeout(timer);
	}, [status, everDegraded]);

	if (status === "live" && !showLive) {
		return null;
	}

	const live = status === "live";
	const offline = status === "offline";

	return (
		<div
			role="status"
			className={cx(
				// Bottom inline-start: the opposite corner from the toasts, so a
				// connection blip during a save does not cover the confirmation.
				"fixed bottom-4 start-4 z-[var(--z-toast)]",
				"flex items-center gap-2 rounded-full border px-3 py-1.5 text-[12px] font-bold shadow-sm",
				live && "border-success-border bg-success-bg text-success-fg",
				!live && !offline && "border-warning-border bg-warning-bg text-warning-fg",
				offline && "border-danger-border bg-danger-bg text-danger-fg",
			)}
		>
			{live ? (
				<Icon as={Wifi} size={13} />
			) : (
				<Icon
					as={offline ? Wifi : Clock}
					size={13}
					// Spins while reconnecting, and stands still for anybody who has
					// asked for less motion — the colour and the words still say it.
					className={
						offline
							? undefined
							: "animate-[km-spin_1.2s_linear_infinite] motion-reduce:animate-none"
					}
				/>
			)}
			{t(live ? "ui.state.live" : offline ? "ui.state.offline" : "ui.state.reconnecting")}
		</div>
	);
}
